import * as d3 from "d3";
import { AXIS_LABEL_SIZE } from "../../constants";
import Utils from '../../Utility/Utils'

export default class MobileScoreGraph {

    constructor(chartArea, data) {
        this.MARGIN = { LEFT: 40, RIGHT: 10, TOP: 20, BOTTOM: 0 };
        this.WIDTH = chartArea.clientWidth;
        this.HEIGHT = window.innerHeight;
        this.data = data
        this.chartArea = chartArea
        this.GRAPH_X_START = 140


        this.totalPoint = document.querySelector('#country-scorecard .score_block .total_points .point')
        this.decimalPoint = document.querySelector('#country-scorecard .score_block .total_points .unit-block .decimal')

        this.topCells = document.querySelectorAll('#country-scorecard .legend-row .axis-grid .cell')
        this.titleBlock = document.querySelector('#country-scorecard .titleBlock .main_title')
        this.countryFlag = document.querySelector('#country-scorecard .titleBlock img')
        this.countryImage = document.querySelector('#country-scorecard .bottom-row .img-block')

        this.svg = d3
            .select("#chart-area")
            .append("svg")
            .attr("width", this.WIDTH)
            .attr("height", this.HEIGHT);

        this.groupBorderColor = {
            Human: "#7054CC",
            Physical: "#007D59",
            Social: "#026CCA",
            Natural: "#B07D24",
            Innovation: "#21405C",
            Institution: "#00B0FF"
        }

        this.groupColors = {
            Human: "#9487E5",
            Physical: "#00A699",
            Social: "#359FFD",
            Natural: "#EDBA00",
            Innovation: "#427DA1",
            Institution: "#4AD1FF"
        };

        this.groupBackgroundColors = {
            Human: "#CCC9ED",
            Physical: "#4ADEC7",
            Social: "#B3DBFE",
            Natural: "#FFDB7A",
            Innovation: "#CCDEEB",
            Institution: "#4DFFFA"
        };

        // Define the groups and their corresponding ranges in `groupedData`
        this.groupRanges = [
            { group: "Human", range: [0, 5] },
            { group: "Physical", range: [7, 11] },
            { group: "Innovation", range: [13, 15] },
            { group: "Institution", range: [17, 18] }, 
            { group: "Natural", range: [20, 22] },
            { group: "Social", range: [24, 24] },
        ];

        if (!this.data || !Array.isArray(this.data)) {
            console.error('Invalid data format for dropdown population:', this.data);
            return;
        } else {
            this.updateGraph("India")
            this.adjustXAxisGrid(chartArea)
        }
    }

    clearGraph() {
        // Clear all elements inside the SVG
        this.svg.selectAll("*").remove();
    }

    updateGraph(country) {
        this.clearGraph()
        this.svg.attr('height', this.HEIGHT)



        const filteredData = this.data.filter(d => d.Country == country);
        const actualData = filteredData[0];

        this.countryFlag.src = `assets/images/Flags/${actualData.Flag}`
        this.titleBlock.textContent = actualData.Country

        const regionEncoded = encodeURIComponent(actualData.Region);
        const countryEncoded = encodeURIComponent(actualData.Country);

        this.countryImage.style.backgroundImage = `url(assets/images/country_images/${regionEncoded}/${countryEncoded}.webp)`

        let numericalDataArray = [];
        let otherDataArray = [];

        for (let key in actualData) {
            if (actualData.hasOwnProperty(key)) {
                if (key === "Total") {
                    continue;
                }

                if (typeof actualData[key] === 'number') {
                    // Push numerical key-value pairs as arrays
                    numericalDataArray.push([key, actualData[key]]);
                } else {
                    // Push non-numerical key-value pairs as arrays
                    otherDataArray.push([key, actualData[key]]);
                }
            }
        }

        let total = numericalDataArray.reduce((sum, [key, value]) => sum + value, 0);

        const integerPart = parseInt(Utils.roundUpToDecimal(total, 1))
        const decimalPart = parseInt(Utils.extractDecimalDigit(total, 1))

        this.totalPoint.textContent = integerPart
        if (decimalPart != undefined) {
            this.decimalPoint.textContent = "." + decimalPart
        } else {
            this.decimalPoint.textContent = ""
        }

        const groupedData = [
            null,
            { label: "Life Expectancy", value: actualData.Life_expectancy, group: "Human" },
            { label: "Suicide", value: actualData.Suicide_mortality, group: "Human" },
            { label: "Tertiary Education", value: actualData.Percent_of_population, group: "Human" },
            { label: "Age Dependency", value: actualData.Age_dependency_ratio, group: "Human" },
            { label: "Human Capital", value: actualData.Human_capital, group: "Human" },
            null, // Gap
            { label: "Physical Capital", value: actualData.Physical_capital, group: "Physical" },
            { label: "Internet Usage", value: actualData.Individuals_using_internet, group: "Physical" },
            { label: "Logistics", value: actualData.World_Bank_logistics, group: "Physical" },
            { label: "Secure Internet", value: actualData.Secure_internet, group: "Physical" },
            { label: "Electricity Access", value: actualData.Access_to_electricity, group: "Physical" },
            null, // Gap
            { label: "Patents", value: actualData.Patent_applications, group: "Innovation" },
            { label: "Science Journals", value: actualData.Science_journal, group: "Innovation" },
            { label: "Sector Concentration", value: actualData.Hirschman_Index, group: "Innovation" },
            null, // Gap
            { label: "Institutional Quality", value: actualData.World_Bank_institutional, group: "Institution" },
            { label: "Inequality", value: actualData.Inequality_principal, group: "Institution" },
            null, // Gap
            { label: "Natural Capital", value: actualData.Natural_capital, group: "Natural" },
            { label: "Pollution", value: actualData.PM2_5, group: "Natural" },
            { label: "Water Stress", value: actualData.Water_stress, group: "Natural" },
            null, // Gap
            { label: "Social Trust", value: actualData.Trust, group: "Social" },
            null
        ];

        const filteredData2 = groupedData.filter(d => d !== null);

        const xScale = d3
            .scaleLinear()
            .domain([d3.min(filteredData2, d => d.value) || 0, d3.max(filteredData2, d => d.value)])
            .range([this.GRAPH_X_START + 10, this.WIDTH - 20]);

        const yScale = d3
            .scaleBand()
            .domain(groupedData.map((_, i) => i))
            .range([this.MARGIN.TOP, this.HEIGHT - this.MARGIN.BOTTOM])
            .padding(0.5);

        this.svg.append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0, ${this.MARGIN.TOP})`)
            .call(d3.axisTop(xScale).ticks(5).tickSize(0).tickPadding(0))
            .selectAll("path, line, text")
            .style('font-size', AXIS_LABEL_SIZE)
            .style("stroke", "none");

        this.svg.append('text')
            .text('Score Value')
            .attr('x', 15)
            .attr('y', this.MARGIN.TOP)
            .style('font-family', 'Logical')
            .style('font-size', AXIS_LABEL_SIZE)


        const baseline = xScale(0);

        const yAxisLabels = this.svg
            .append("g")
            .attr("class", "y-axis-labels")
            .selectAll("text")
            .data(groupedData)
            .join("text")
            .attr("x", this.GRAPH_X_START - 10) // Positioning labels to the left of the bars
            .attr("y", (d, i) => yScale(i) + yScale.bandwidth() / 2)
            .attr("dy", "0.35em") // Center the text vertically
            .attr("text-anchor", "end") // Align text to the right of the x position
            .attr("fill", "#000")
            .text(d => d ? d.label : "");

        const yAxisPoints = this.svg
            .append("g")
            .attr("class", "y-axis-labels")
            .selectAll("text")
            .data(groupedData)
            .join("text")
            .attr("x", this.MARGIN.LEFT - 15) // Positioning labels to the left of the bars
            .attr("y", (d, i) => yScale(i) + yScale.bandwidth() / 2)
            .attr("dy", "0.35em") // Center the text vertically
            .attr("text-anchor", "end") // Align text to the right of the x position
            .attr("fill", "#000")
            .text(d => d ? parseFloat(Utils.roundUpToDecimal(d.value, 1)) : "");

        yAxisLabels.style("font-size", `${AXIS_LABEL_SIZE}px`);
        yAxisPoints.style("font-size", `${AXIS_LABEL_SIZE}px`);

        this.xLine = this.svg
            .append("line")
            .attr("y1", this.MARGIN.LEFT)
            .attr("y2", this.HEIGHT)
            .attr("x1", baseline > this.WIDTH - 15 ? this.WIDTH - 15 : baseline)
            .attr("x2", baseline > this.WIDTH - 15 ? this.WIDTH - 15 : baseline)
            .attr("stroke", "#000")
            .attr("stroke-width", 1);

        this.baseline = this.svg
            .append("line")
            .attr("y1", this.MARGIN.LEFT)
            .attr("y2", this.HEIGHT)
            .attr("x1", this.GRAPH_X_START)
            .attr("x2", this.GRAPH_X_START)
            .attr("stroke", "rgba(0, 0, 0, 1)")
            .attr("stroke-dasharray", "2,2")
            .attr("stroke-width", 1);

        const bgGroup = this.svg.append("g").attr("class", "bg-group");

        this.groupRanges.forEach(({ group, range }, index) => {
            const nullWidth = yScale.bandwidth() / 2; // Half the width of a null space

            bgGroup
                .append("rect")
                .attr('class', 'bg-rect')
                .attr(
                    "y",
                    index === 0
                        ? yScale(range[0]) + 10 // No adjustment for the first rectangle
                        : yScale(range[0]) - nullWidth * 3 // Adjust for other rectangles
                )
                .attr("x", 0) // Top of the chart
                .attr(
                    "height",
                    index === 0
                        ? yScale(range[1] + 1) - yScale(range[0]) + nullWidth - 10 // No left null adjustment for the first rectangle
                        : yScale(range[1] + 1) - yScale(range[0]) + nullWidth * 4 // Include null space on both sides
                )
                .attr("width", this.WIDTH) // Height spans the chart
                .attr("fill", this.groupColors[group]) // Background color for the group
                .attr("opacity", 0.1); // Adjust opacity for visibility
        });

        this.svg
            .append("g")
            .attr("class", "line-group")
            .selectAll("line")
            .data(groupedData)
            .join("line")
            .attr("y1", (d, i) => yScale(i) + yScale.bandwidth() / 2) // Center of the bar
            .attr("y2", (d, i) => yScale(i) + yScale.bandwidth() / 2) // Same as x1
            .attr("x1", this.GRAPH_X_START - 5) // Start at the top of the chart
            .attr("x2", this.WIDTH) // End at the bottom of the chart
            .attr("stroke", "rgba(0, 0, 0, 0.1)") // Line color
            .attr("stroke-width", d => (d ? 1 : 0)) // Line width
            .attr("stroke-dasharray", "4 4"); // Dashed line pattern

        this.svg
            .append("g")
            .selectAll("rect")
            .data(groupedData)
            .join("rect")
            .attr("x", xScale(0))
            .attr("y", (d, i) => yScale(i))
            .attr("height", yScale.bandwidth())
            .attr("width", 0)
            .attr("fill", d => (d ? this.groupBackgroundColors[d.group] : "none"))
            .transition()
            .duration(1000)
            .ease(d3.easePoly.exponent(1))
            .attr("x", d => (d ? (d.value >= 0 ? xScale(0) : xScale(d.value)) : xScale(0)))
            .attr("width", d => (d ? Math.abs(xScale(d.value) - xScale(0)) : 0));



        this.svg
            .append("g")
            .selectAll("circle")
            .data(groupedData)
            .join("circle")
            .attr("cx", baseline)
            .attr("cy", (d, i) => yScale(i) + yScale.bandwidth() / 2)
            .attr("r", 6)
            .attr("fill", d => (d ? this.groupColors[d.group] : "none"))
            .attr("stroke", d => (d ? this.groupBorderColor[d.group] : "none"))
            .attr("stroke-width", 2)
            .transition()
            .duration(1000)
            .ease(d3.easePoly.exponent(1))
            .attr("cx", d => (d ? xScale(d.value) : baseline))

        this.updateTopLegendValues(groupedData)
    }

    updateTopLegendValues(data) {
        const groupSums = {};

        // Iterate through groupedData
        data.forEach(item => {
            if (item !== null) {  // Skip null values
                const { value, group } = item;

                // If the group already exists in groupSums, add the value
                if (groupSums[group]) {
                    groupSums[group] += value;
                } else {
                    // If the group doesn't exist, create it and set the initial value
                    groupSums[group] = value;
                }
            }
        });


        this.topCells[0].querySelector('.cell-point .point').textContent = Utils.roundUpToDecimal(groupSums["Human"], 1)
        this.topCells[1].querySelector('.cell-point .point').textContent = Utils.roundUpToDecimal(groupSums["Physical"], 1)
        this.topCells[2].querySelector('.cell-point .point').textContent = Utils.roundUpToDecimal(groupSums["Innovation"], 1)
        this.topCells[3].querySelector('.cell-point .point').textContent = Utils.roundUpToDecimal(groupSums["Institution"], 1)
        this.topCells[4].querySelector('.cell-point .point').textContent = Utils.roundUpToDecimal(groupSums["Natural"], 1)
        this.topCells[5].querySelector('.cell-point .point').textContent = Utils.roundUpToDecimal(groupSums["Social"], 1)

    }

    adjustXAxisGrid(chartArea) {
        const lineGroupBBox = chartArea.querySelector('svg .bg-group').getBBox()
        const bgRectBBox = chartArea.querySelectorAll('svg .bg-rect')
        // const bottomFirstCol = document.querySelector('#country-scorecard .axis-lables .first-col')
        // const bottomAxisGrid = document.querySelector('#country-scorecard .axis-lables .axis-grid')
        // bottomAxisGrid.style.width = `${lineGroupBBox.width}px`
        // bottomFirstCol.style.width = `${lineGroupBBox.x}px`

        const topFirstCol = document.querySelector('#country-scorecard .legend-row .first-col')
        const topAxisGrid = document.querySelector('#country-scorecard .legend-row .axis-grid')
        topAxisGrid.style.height = `${lineGroupBBox.height}px`
        topFirstCol.style.height = `${lineGroupBBox.y}px`
        const axisGridCells = document.querySelectorAll('#country-scorecard .legend-row .axis-grid .cell')
        bgRectBBox.forEach((rect, i) => {
            const percent = (rect.getBBox().height * 100) / topAxisGrid.offsetHeight
            axisGridCells[i].style.height = `${Utils.truncateToDecimals(percent, 1)}%`
        })
        // Adjudt Overlay Lines 
        // const overlayLines = document.querySelector('#country-scorecard .graph-area .overlay-lines')
        // overlayLines.style.width = `${lineGroupBBox.width}px`
        // overlayLines.style.left = `${lineGroupBBox.x}px`
    }
}   